import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import { decodeJWT, isTokenExpired, shouldRefreshToken, getTokenTimeRemaining, getTokenInfo } from '~/utils/jwt'
import { deserializeResponse } from '~/utils/api/deserializeResponse'
import { sanitizeControlChars } from '~/utils/sanitizeControlChars'
import { usePermissionsApi } from '~/composables/api/usePermissionsApi'
import { useApi } from '~/composables/api/useApi'
import { useRolePermissions } from '~/composables/useRolePermissions'

export const useAuthStore = defineStore('auth', {
  // State simple (no computed de cookies como en Facturaly)
  state: () => ({
    accessToken: null,
    refreshToken: null,
    user: null,
    isLoadingUser: false,
    permissions: null,
    promotionRoles: {},
    activePromotionId: null,
    isImpersonating: false,
    realUser: null
  }),

  getters: {
    isAuthenticated: (state) => !!state.accessToken,

    companyId: (state) => {
      if (!state.accessToken) return null
      const decoded = decodeJWT(state.accessToken)
      return decoded?.companyId ?? null
    },

    userId: (state) => {
      if (!state.accessToken) return null
      const decoded = decodeJWT(state.accessToken)
      return decoded?.sub ?? null
    },

    role: (state) => {
      if (!state.accessToken) return null
      const decoded = decodeJWT(state.accessToken)
      return decoded?.role ?? null
    },

    isAccessTokenExpired: (state) => {
      if (!state.accessToken) return true
      return isTokenExpired(state.accessToken)
    },

    isRefreshTokenExpired: (state) => {
      if (!state.refreshToken) return true
      return isTokenExpired(state.refreshToken, 0)
    },

    needsRefresh: (state) => {
      if (!state.accessToken) return false
      return shouldRefreshToken(state.accessToken)
    },

    tokenTimeRemaining: (state) => {
      if (!state.accessToken) return 0
      return getTokenTimeRemaining(state.accessToken)
    },

    activeRole: (state) => {
      if (state.activePromotionId && state.promotionRoles[state.activePromotionId]) {
        return state.promotionRoles[state.activePromotionId].role
      }
      return state.role
    },

    impersonationState: (state) => ({
      isImpersonating: state.isImpersonating,
      realUser: state.realUser
    }),
  },

  actions: {
    /**
     * Guarda los tokens de autenticación y los persiste en cookies
     */
    setTokens(tokens) {
      this.accessToken = tokens.accessToken
      this.refreshToken = tokens.refreshToken

      // Persistir en cookies
      const accessTokenCookie = useCookie('accessToken', {
        maxAge: 60 * 60,
        secure: import.meta.env.PROD,
        sameSite: 'strict',
      })
      const refreshTokenCookie = useCookie('refreshToken', {
        maxAge: 60 * 60 * 24 * 7,
        secure: import.meta.env.PROD,
        sameSite: 'strict',
      })

      accessTokenCookie.value = tokens.accessToken
      refreshTokenCookie.value = tokens.refreshToken
    },

    /**
     * Establece los datos del usuario
     */
    setUser(userData) {
      const sanitizedUserData = userData ? sanitizeControlChars(userData) : null
      this.user = sanitizedUserData

      // Persistir en cookie
      const userCookie = useCookie('user', {
        maxAge: 60 * 60 * 24 * 7,
        secure: import.meta.env.PROD,
        sameSite: 'strict',
      })
      userCookie.value = sanitizedUserData ? JSON.stringify(sanitizedUserData) : null

      // Identificar usuario en Sentry
      if (import.meta.client && sanitizedUserData) {
        Sentry.setUser({
          id: sanitizedUserData.id,
          email: sanitizedUserData.email,
          role: this.role,
          companyId: this.companyId,
        })
      }
    },

    /**
     * Limpia toda la autenticación y datos almacenados
     */
    clearAuth() {
      // Limpiar state del store
      this.accessToken = null
      this.refreshToken = null
      this.user = null
      this.permissions = null
      this.promotionRoles = {}
      this.activePromotionId = null
      this.isImpersonating = false
      this.realUser = null

      // Limpiar cookies
      const accessTokenCookie = useCookie('accessToken')
      const refreshTokenCookie = useCookie('refreshToken')
      const userCookie = useCookie('user')

      accessTokenCookie.value = null
      refreshTokenCookie.value = null
      userCookie.value = null

      // Limpiar sessionStorage (solo en cliente)
      if (import.meta.client) {
        // W13: solo las keys propias — el clear() total del origen también
        // borraba el storage de cualquier otra app servida desde este dominio.
        // Keys de la app: `pendingBc3File` (BC3 arrastrado al listado → alta).
        sessionStorage.removeItem('pendingBc3File')

        // Limpiar usuario de Sentry
        Sentry.setUser(null)
      }

      // Limpiar permisos del composable
      const { clearPermissions } = useRolePermissions()
      clearPermissions()

      // Limpiar otros stores
      const configStore = useConfigStore()
      configStore.clearConfig()

      const breadcrumbStore = useBreadcrumbStore()
      breadcrumbStore.clearBreadcrumb()

      const categoryStore = useCompanyCategoryStore()
      categoryStore.clearCategories()
    },

    /**
     * Obtiene los datos del usuario desde /me
     */
    async fetchUser() {
      if (!this.accessToken) {
        throw new Error('No hay access token disponible')
      }

      this.isLoadingUser = true
      try {
        const config = useRuntimeConfig()
        const response = await $fetch('/auth/me', {
          method: 'GET',
          baseURL: config.public.apiBase,
          headers: {
            Authorization: `Bearer ${this.accessToken}`
          }
        })

        const userData = await deserializeResponse(response)
        this.setUser(userData)

        // Detectar impersonación: `isImpersonating` y `realUser` viajan en la raíz
        // de la respuesta, fuera de `data`, y deserializeResponse solo devuelve `data`
        this.setImpersonationState(response)

        return userData
      } catch (error) {
        if (error.response?.status === 401) {
          this.clearAuth()
        }

        throw error
      } finally {
        this.isLoadingUser = false
      }
    },

    /**
     * Realiza el login completo
     */
    async login(email, password) {
      const config = useRuntimeConfig()

      try {
        const loginResponse = await $fetch('/auth/login', {
          method: 'POST',
          baseURL: config.public.apiBase,
          body: { email, password },
        })

        // Deserializar respuesta JSONAPI
        const tokensData = await deserializeResponse(loginResponse)

        // Guardar tokens
        this.setTokens({
          accessToken: tokensData.accessToken,
          refreshToken: tokensData.refreshToken,
        })

        // Obtener datos del usuario
        await this.fetchUser()

        // Cargar configuración de la aplicación
        const configStore = useConfigStore()
        await configStore.loadConfig()

        // Cargar permisos del usuario
        await this.loadPermissions()

        return tokensData
      } catch (error) {
        this.clearAuth()
        throw error
      }
    },

    /**
     * Login rápido SOLO dev (local/dev): entra como el usuario indicado sin password.
     * El endpoint /dev/quick-login solo existe si el backend tiene QUICK_LOGIN_ENABLED.
     */
    async quickLogin(userId) {
      try {
        const { makeRequest } = useApi()
        const loginResponse = await makeRequest({
          url: '/dev/quick-login/login',
          method: 'POST',
          data: { userId },
          _skipAuth: true,
        })

        const tokensData = await deserializeResponse(loginResponse)

        this.setTokens({
          accessToken: tokensData.accessToken,
          refreshToken: tokensData.refreshToken,
        })

        await this.fetchUser()

        const configStore = useConfigStore()
        await configStore.loadConfig()

        await this.loadPermissions()

        return tokensData
      } catch (error) {
        this.clearAuth()
        throw error
      }
    },

    /**
     * Refresca el access token usando el refresh token
     */
    async refreshAccessToken() {

      if (!this.refreshToken) {
        throw new Error('No hay refresh token disponible')
      }

      const config = useRuntimeConfig()

      try {
        const response = await $fetch('/auth/refresh', {
          method: 'POST',
          baseURL: config.public.apiBase,
          body: { refreshToken: this.refreshToken },
        })

        // Deserializar respuesta JSONAPI
        const tokensData = await deserializeResponse(response)

        this.setTokens({
          accessToken: tokensData.accessToken,
          refreshToken: tokensData.refreshToken,
        })

        await this.fetchUser()

        return tokensData
      } catch (error) {
        this.clearAuth()
        throw error
      }
    },

    async restoreSession(tokens) {

      // Establecer tokens en el state
      this.accessToken = tokens.accessToken
      this.refreshToken = tokens.refreshToken

      // Verificar si el access token está expirado
      if (isTokenExpired(this.accessToken)) {

        // Verificar si el refresh token también está expirado
        if (isTokenExpired(this.refreshToken, 0)) {
          this.clearAuth()
          throw new Error('session_expired')
        }

        // Intentar refresh
        await this.refreshAccessToken()
      } else {
        // Token válido, obtener datos del usuario
        // Primero intentar restaurar usuario desde cookie
        const userCookie = useCookie('user')
        if (userCookie.value) {
          try {
            this.user = typeof userCookie.value === 'string'
              ? JSON.parse(userCookie.value)
              : userCookie.value
          } catch {
            // Cookie corrupta (p. ej. salto de línea no escapado); limpiar y rehidratar desde API
            userCookie.value = null
            this.user = null
          }
        }

        // Luego refrescar datos desde API
        await this.fetchUser()
      }
    },

    getTokenStatus() {
      return {
        accessToken: this.accessToken ? getTokenInfo(this.accessToken) : null,
        refreshToken: this.refreshToken ? getTokenInfo(this.refreshToken) : null,
      }
    },

    async loadPermissions() {
      if (!this.accessToken) {
        return null
      }

      try {
        const { getMyPermissions } = usePermissionsApi()
        const permissionsData = await getMyPermissions()

        this.permissions = permissionsData.permissions
        const rolesArray = permissionsData.promotionRoles || []
        this.promotionRoles = Array.isArray(rolesArray)
          ? rolesArray.reduce((acc, entry) => { acc[entry.promotionId] = entry; return acc }, {})
          : rolesArray

        const { setPermissions } = useRolePermissions()
        setPermissions(permissionsData)

        return permissionsData
      } catch (error) {
        console.error('[AuthStore] Error loading permissions:', error)
        throw error
      }
    },

    setActivePromotion(promotionId) {
      this.activePromotionId = promotionId
      const { setActivePromotion: setRolePromotion } = useRolePermissions()
      setRolePromotion(promotionId)
    },

    setImpersonationState(userData) {
      this.isImpersonating = userData.isImpersonating || false
      this.realUser = userData.realUser || null
    },

    clearImpersonationState() {
      this.isImpersonating = false
      this.realUser = null
    },
  }
})
